import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { baseApiURL } from "../../baseUrl";

const defaultStop = () => ({
  name: "",
  landmark: "",
  distanceKm: "",
  fare: "",
  arrivalTime: "",
});

const initialFormState = {
  busNumber: "",
  busName: "",
  driverName: "",
  driverPhone: "",
  capacity: 40,
  routeName: "",
  description: "",
  baseFare: 0,
  status: "active",
  stops: [defaultStop()],
};

const ManageRoutes = () => {
  const [routes, setRoutes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(initialFormState);
  const [editingId, setEditingId] = useState(null);

  const headers = useMemo(() => ({ "Content-Type": "application/json" }), []);

  useEffect(() => {
    fetchRoutes();
  }, []);

  const fetchRoutes = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${baseApiURL()}/transport/routes`, { headers });
      if (res.data.success) {
        setRoutes(res.data.routes || []);
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Unable to load transport data.");
    } finally {
      setLoading(false);
    }
  };

  const updateFormField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateStopField = (index, field, value) => {
    setForm((prev) => {
      const updatedStops = [...prev.stops];
      updatedStops[index] = { ...updatedStops[index], [field]: value };
      return { ...prev, stops: updatedStops };
    });
  };

  const addStop = () => {
    setForm((prev) => ({ ...prev, stops: [...prev.stops, defaultStop()] }));
  };

  const removeStop = (index) => {
    setForm((prev) => ({
      ...prev,
      stops: prev.stops.filter((_, idx) => idx !== index),
    }));
  };

  const resetForm = () => {
    setForm(initialFormState);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      capacity: Number(form.capacity),
      baseFare: Number(form.baseFare),
      stops: form.stops.map((stop) => ({
        ...stop,
        distanceKm: stop.distanceKm === "" ? "" : Number(stop.distanceKm),
        fare: stop.fare === "" ? "" : Number(stop.fare),
      })),
    };

    try {
      const url = `${baseApiURL()}/transport/routes${editingId ? `/${editingId}` : ""}`;
      const method = editingId ? axios.put : axios.post;
      const res = await method(url, payload, { headers });
      if (res.data.success) {
        toast.success(res.data.message);
        resetForm();
        fetchRoutes();
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Unable to save data.");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (route) => {
    setEditingId(route._id);
    setForm({
      busNumber: route.busNumber,
      busName: route.busName,
      driverName: route.driverName || "",
      driverPhone: route.driverPhone || "",
      capacity: route.capacity,
      routeName: route.routeName,
      description: route.description || "",
      baseFare: route.baseFare,
      status: route.status,
      stops: route.stops.length ? route.stops : [defaultStop()],
    });
  };

  const handleDelete = async (routeId) => {
    if (!window.confirm("Delete this route? This cannot be undone.")) {
      return;
    }
    try {
      const res = await axios.delete(`${baseApiURL()}/transport/routes/${routeId}`, { headers });
      if (res.data.success) {
        toast.success("Route deleted.");
        fetchRoutes();
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Unable to delete route.");
    }
  };

  return (
    <div className="space-y-6">
      <div className="bento-header-banner flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">Transport Management</h1>
          <p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">
            Add buses, define routes with stops/stations, and publish fares for students.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <form
          onSubmit={handleSubmit}
          className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xs p-6 space-y-6"
        >
          <div className="flex items-center justify-between">
            <h2 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">
              {editingId ? "Update Transport Route" : "Create New Transport Route"}
            </h2>
            {editingId && (
              <button
                type="button"
                onClick={resetForm}
                className="text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer"
              >
                Cancel edit
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input
              required
              value={form.busNumber}
              onChange={(e) => updateFormField("busNumber", e.target.value)}
              placeholder="Bus Number"
              className="px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <input
              required
              value={form.busName}
              onChange={(e) => updateFormField("busName", e.target.value)}
              placeholder="Bus / Service Name"
              className="px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <input
              value={form.driverName}
              onChange={(e) => updateFormField("driverName", e.target.value)}
              placeholder="Driver Name"
              className="px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <input
              value={form.driverPhone}
              onChange={(e) => updateFormField("driverPhone", e.target.value)}
              placeholder="Driver Contact"
              className="px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <input
              type="number"
              min="1"
              value={form.capacity}
              onChange={(e) => updateFormField("capacity", e.target.value)}
              placeholder="Total Seats"
              className="px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <input
              required
              value={form.routeName}
              onChange={(e) => updateFormField("routeName", e.target.value)}
              placeholder="Route Name"
              className="px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <input
              type="number"
              min="0"
              value={form.baseFare}
              onChange={(e) => updateFormField("baseFare", e.target.value)}
              placeholder="Base Fare"
              className="px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <select
              value={form.status}
              onChange={(e) => updateFormField("status", e.target.value)}
              className="px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="active">Active</option>
              <option value="maintenance">Maintenance</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <textarea
            value={form.description}
            onChange={(e) => updateFormField("description", e.target.value)}
            placeholder="Notes or description for this route"
            className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
            rows={3}
          />

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">Stops & Pricing</h3>
              <button
                type="button"
                onClick={addStop}
                className="text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer"
              >
                + Add Stop
              </button>
            </div>

            {form.stops.map((stop, index) => (
              <div key={index} className="border border-slate-200 dark:border-slate-800 rounded-xl p-3.5 space-y-3 bg-slate-50 dark:bg-slate-950">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-700 dark:text-slate-300">Stop #{index + 1}</span>
                  {form.stops.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeStop(index)}
                      className="text-xs font-bold text-rose-600 dark:text-rose-400 hover:underline cursor-pointer"
                    >
                      Remove
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                  <input
                    required
                    value={stop.name}
                    onChange={(e) => updateStopField(index, "name", e.target.value)}
                    placeholder="Stop Name"
                    className="px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <input
                    value={stop.landmark || ""}
                    onChange={(e) => updateStopField(index, "landmark", e.target.value)}
                    placeholder="Landmark / Station"
                    className="px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <input
                    type="number"
                    min="0"
                    value={stop.distanceKm}
                    onChange={(e) => updateStopField(index, "distanceKm", e.target.value)}
                    placeholder="Distance (km)"
                    className="px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <input
                    type="number"
                    min="0"
                    value={stop.fare}
                    onChange={(e) => updateStopField(index, "fare", e.target.value)}
                    placeholder="Fare (optional)"
                    className="px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <input
                  value={stop.arrivalTime || ""}
                  onChange={(e) => updateStopField(index, "arrivalTime", e.target.value)}
                  placeholder="Arrival Time (e.g. 7:45 AM)"
                  className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            ))}
          </div>

          <button
            type="submit"
            disabled={saving}
            className="w-full bg-indigo-600 hover:bg-indigo-500 text-white py-2.5 rounded-xl font-bold text-xs shadow-xs transition cursor-pointer disabled:opacity-60"
          >
            {saving ? "Saving..." : editingId ? "Update Route" : "Save Route"}
          </button>
        </form>

        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xs p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">Published Routes</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">Overview of buses and stops visible to students.</p>
            </div>
            <button
              type="button"
              onClick={fetchRoutes}
              className="text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer"
            >
              Refresh
            </button>
          </div>

          {loading ? (
            <p className="text-xs text-slate-400">Loading routes...</p>
          ) : !routes.length ? (
            <div className="text-center py-12 text-slate-400 text-xs border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl">
              No routes added yet. Create one using the form.
            </div>
          ) : (
            <div className="space-y-4">
              {routes.map((route) => {
                const occupied = route.allocatedSeats || 0;
                const seatsLeft = Math.max(route.capacity - occupied, 0);
                return (
                  <div key={route._id} className="border border-slate-200 dark:border-slate-800 rounded-2xl p-4 space-y-3 bg-slate-50/50 dark:bg-slate-950/50">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                          {route.routeName} • {route.busNumber}
                        </h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{route.busName}</p>
                      </div>
                      <span
                        className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full border ${
                          route.status === "active"
                            ? "bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800"
                            : route.status === "maintenance"
                            ? "bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800"
                            : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700"
                        }`}
                      >
                        {route.status}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                      <div>
                        <p className="text-slate-400 dark:text-slate-500 font-semibold">Seats</p>
                        <p className="font-bold text-slate-800 dark:text-slate-200">
                          {occupied}/{route.capacity}{" "}
                          <span className="text-[10px] text-slate-400">(Left: {seatsLeft})</span>
                        </p>
                      </div>
                      <div>
                        <p className="text-slate-400 dark:text-slate-500 font-semibold">Base Fare</p>
                        <p className="font-bold text-slate-800 dark:text-slate-200">₹{route.baseFare}</p>
                      </div>
                      <div>
                        <p className="text-slate-400 dark:text-slate-500 font-semibold">Stops</p>
                        <p className="font-bold text-slate-800 dark:text-slate-200">{route.stops.length}</p>
                      </div>
                      <div>
                        <p className="text-slate-400 dark:text-slate-500 font-semibold">Driver</p>
                        <p className="font-bold text-slate-800 dark:text-slate-200">{route.driverName || "NA"}</p>
                      </div>
                    </div>

                    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-3 space-y-2 max-h-40 overflow-y-auto">
                      {route.stops.map((stop) => (
                        <div key={stop.code || stop.name} className="flex items-center justify-between text-xs">
                          <div>
                            <p className="font-bold text-slate-800 dark:text-slate-200">{stop.name}</p>
                            {stop.landmark && <p className="text-slate-400 text-[11px]">{stop.landmark}</p>}
                          </div>
                          <div className="text-right">
                            {typeof stop.fare === "number" && (
                              <p className="text-slate-800 dark:text-slate-200 font-bold">₹{stop.fare}</p>
                            )}
                            {stop.distanceKm && (
                              <p className="text-slate-400 text-[10px]">{stop.distanceKm} km</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleEdit(route)}
                        className="px-3.5 py-1.5 rounded-xl border border-slate-200 dark:border-slate-800 text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(route._id)}
                        className="px-3.5 py-1.5 rounded-xl border border-rose-200 dark:border-rose-800 text-xs font-bold text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition cursor-pointer"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageRoutes;

